/**
 * Account / Billing page — subscription status + writer profile (used in PDF title page).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/Header";
import { TrialBanner } from "@/components/TrialBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { ElevenLabsSection } from "@/components/ElevenLabsSection";
import { toast } from "sonner";

export default function Account() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  // --- Profile form ---
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    has_representation: false,
    rep_name: "", rep_company: "", rep_email: "", rep_phone: "", rep_address: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      address: user.address || "",
      has_representation: !!user.has_representation,
      rep_name: user.rep_name || "",
      rep_company: user.rep_company || "",
      rep_email: user.rep_email || "",
      rep_phone: user.rep_phone || "",
      rep_address: user.rep_address || "",
    });
  }, [user]);

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const saveProfile = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", form);
      await refresh();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const startCheckout = async () => {
    setLoading("checkout");
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setLoading(null);
    }
  };

  const openPortal = async () => {
    setLoading("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not open portal");
      setLoading(null);
    }
  };

  if (!user) return null;

  const status = user.subscription_status;
  const daysLeft = user.trial_days_remaining ?? null;

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <ImpersonationBanner />
      <TrialBanner />
      <AppHeader />

      <div className="max-w-[800px] mx-auto px-6 py-10 md:py-14">
        <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-3">Your account</p>
        <h1 className="font-display text-4xl md:text-5xl mb-10" data-testid="account-title">Profile & billing</h1>

        {/* ----- Writer profile ----- */}
        <section className="bg-white border border-[var(--line)] rounded-md p-8 mb-6" data-testid="profile-section">
          <h2 className="font-display text-2xl mb-1">Your profile</h2>
          <p className="text-sm text-[var(--ink-secondary)] mb-6">
            This info appears on your PDF title page. If you have representation, that contact info is used instead of yours.
          </p>

          <form onSubmit={saveProfile} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name">
                <Input value={form.name} onChange={(v) => update({ name: v })} testId="profile-name" />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(v) => update({ email: v })} testId="profile-email" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(v) => update({ phone: v })} placeholder="(555) 123-4567" testId="profile-phone" />
              </Field>
              <Field label="Address">
                <Textarea value={form.address} onChange={(v) => update({ address: v })} placeholder="123 Vine Street&#10;Los Angeles, CA 90028" testId="profile-address" />
              </Field>
            </div>

            <div className="border-t border-[var(--line)] pt-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.has_representation}
                  onChange={(e) => update({ has_representation: e.target.checked })}
                  data-testid="profile-rep-toggle"
                  className="accent-[var(--brand)] w-4 h-4 mt-1"
                />
                <div>
                  <div className="text-sm font-medium text-[var(--ink-primary)]">I have representation</div>
                  <div className="text-[11px] text-[var(--ink-muted)] mt-0.5">When checked, the agency / manager's contact replaces yours on the title page.</div>
                </div>
              </label>

              {form.has_representation && (
                <div className="grid md:grid-cols-2 gap-5 mt-5" data-testid="profile-rep-block">
                  <Field label="Representative name">
                    <Input value={form.rep_name} onChange={(v) => update({ rep_name: v })} placeholder="Jane Smith" testId="profile-rep-name" />
                  </Field>
                  <Field label="Company / Agency">
                    <Input value={form.rep_company} onChange={(v) => update({ rep_company: v })} placeholder="Creative Artists Agency" testId="profile-rep-company" />
                  </Field>
                  <Field label="Representative email">
                    <Input type="email" value={form.rep_email} onChange={(v) => update({ rep_email: v })} testId="profile-rep-email" />
                  </Field>
                  <Field label="Representative phone">
                    <Input value={form.rep_phone} onChange={(v) => update({ rep_phone: v })} testId="profile-rep-phone" />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Representative address">
                      <Textarea value={form.rep_address} onChange={(v) => update({ rep_address: v })} testId="profile-rep-address" />
                    </Field>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button type="submit" disabled={saving} className="btn-brand disabled:opacity-60" data-testid="profile-save-btn">
                {saving ? "Saving…" : "Save profile"}
              </button>
            </div>
          </form>
        </section>

        {/* ----- Subscription ----- */}
        <section className="bg-white border border-[var(--line)] rounded-md p-8 mb-6" data-testid="billing-section">
          <h2 className="font-display text-2xl mb-1">Billing</h2>
          <div className="flex items-start justify-between flex-wrap gap-4 mt-4">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-1">Current status</p>
              <StatusBadge status={status} comp={user.comp_granted} />
            </div>
          </div>

          {status === "trialing" && daysLeft !== null && (
            <p className="mt-5 text-[var(--ink-secondary)]" data-testid="account-trial-status">
              <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong> left in your free trial.
            </p>
          )}
          {status === "active" && (
            <p className="mt-5 text-[var(--ink-secondary)]" data-testid="account-active-status">
              You're on the Writer plan — $19.97/month. Manage via the Stripe portal.
            </p>
          )}
          {(status === "locked" || status === "past_due") && (
            <p className="mt-5 text-[var(--ink-secondary)]" data-testid="account-locked-status">
              {status === "locked" ? "Add a card to unlock writing again." : "Your last payment didn't go through. Update your card."}
            </p>
          )}
          {user.comp_granted && (
            <p className="mt-5 text-[var(--ok)] text-sm" data-testid="account-comp-status">
              You have complimentary access.
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {status !== "active" && !user.comp_granted && (
              <button onClick={startCheckout} disabled={loading === "checkout"} className="btn-brand disabled:opacity-60" data-testid="account-checkout-btn">
                {loading === "checkout" ? "Opening Stripe…" : "Add payment method"}
              </button>
            )}
            {(status === "active" || status === "past_due" || user.payment_method_added) && (
              <button onClick={openPortal} disabled={loading === "portal"} className="btn-ghost disabled:opacity-60" data-testid="account-portal-btn">
                {loading === "portal" ? "Opening portal…" : "Manage billing"}
              </button>
            )}
            <button onClick={refresh} className="btn-ghost" data-testid="account-refresh-btn">Refresh status</button>
          </div>
        </section>

        <ElevenLabsSection />

        <section className="bg-white border border-[var(--line)] rounded-md p-8">
          <h2 className="font-display text-2xl mb-3">Your work is yours.</h2>
          <p className="text-[var(--ink-secondary)] text-sm leading-relaxed">
            Whether you're paying or not, you can always export your scripts to PDF, Final Draft (.fdx), or Fountain.
          </p>
          <button onClick={() => navigate("/app")} className="btn-ghost mt-5" data-testid="account-back-btn">Back to scripts</button>
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Input({ type = "text", value, onChange, placeholder, testId }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-2 text-sm"
    />
  );
}

function Textarea({ value, onChange, placeholder, testId }) {
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-2 text-sm min-h-[60px] resize-none leading-relaxed"
    />
  );
}

function StatusBadge({ status, comp }) {
  if (comp) return <Badge color="ok" label="Comp access" testId="status-badge" />;
  const map = {
    trialing: { color: "warn", label: "Free trial" },
    active: { color: "ok", label: "Active" },
    past_due: { color: "err", label: "Payment failed" },
    locked: { color: "err", label: "Locked" },
    canceled: { color: "muted", label: "Canceled" },
    comped: { color: "ok", label: "Comp access" },
  };
  const { color, label } = map[status] || { color: "muted", label: status };
  return <Badge color={color} label={label} testId="status-badge" />;
}

function Badge({ color, label, testId }) {
  const colors = {
    ok: "text-[var(--ok)] bg-[var(--ok)]/10 border-[var(--ok)]/30",
    warn: "text-[var(--warn)] bg-[var(--warn)]/15 border-[var(--warn)]/40",
    err: "text-[var(--err)] bg-[var(--err)]/10 border-[var(--err)]/30",
    muted: "text-[var(--ink-muted)] bg-[var(--warm-accent)] border-[var(--line)]",
  };
  return (
    <span data-testid={testId} className={`inline-block text-xs uppercase tracking-widest px-2.5 py-1 rounded border ${colors[color] || colors.muted}`}>
      {label}
    </span>
  );
}
